import { computed, onMounted, reactive, ref } from 'vue'
import { cloneDeep, isArray } from '@/global/utils/utils'
import { useWorkOrderDetailsApi } from './useWorkOrderDetailsApi'
import { ROAD_NAME_KEY_PREFIX } from './useRequestDetailsFields'
import { useInfoDialog } from '@/global/composables/dialogs/useInfoDialog'
import { useAppContext } from '@/global/services/compositionApi/useAppContext'
import { dateService, FORMATS } from '@/global/services/date'

const TRANSLATION_NAMESPACE = 'road-maintenance/temporary_regulation/work_order_details'

let uidCounter = 0

// Distinct from the `${prefix}-${id}` uids used for persisted records
// (e.g. 'phase-1'), so a freshly created, not-yet-saved entity can never
// collide with one already loaded from the backend.
function nextUid (prefix) {
  uidCounter += 1
  return `${prefix}-new-${uidCounter}`
}

// Fields are edited via the DD.MM.YYYY. HH:mm datetime-picker format, but the
// API expects Y-m-d H:i:s. Falls back to a generic parse for values that
// already arrived from the backend in that (or another dayjs-parsable) shape.
function toApiDateTime (value) {
  if (!value) {
    return null
  }
  if (dateService.isValid(value, FORMATS.datetimeShort, true)) {
    return dateService.formatCustom(value, FORMATS.datetimeShort, FORMATS.isoDatetime)
  }
  if (dateService.isValid(value)) {
    return dateService.format(value, FORMATS.isoDatetime)
  }
  return value
}

// Inverse of toApiDateTime — the backend returns Y-m-d H:i:s, but the
// datetime-picker fields display/edit values in DD.MM.YYYY. HH:mm.
function toDisplayDateTime (value) {
  if (!value) {
    return ''
  }
  if (dateService.isValid(value, FORMATS.isoDatetime, true)) {
    return dateService.formatCustom(value, FORMATS.isoDatetime, FORMATS.datetimeShort)
  }
  if (dateService.isValid(value)) {
    return dateService.format(value, FORMATS.datetimeShort)
  }
  return value
}

// A location's `planned_duration` is edited via a time-only picker (HH:mm),
// but the API stores it as a plain integer number of seconds.
function toApiDurationSeconds (value) {
  if (!value) {
    return null
  }
  if (!dateService.isValid(value, FORMATS.timeShort, true)) {
    return null
  }
  const [hours, minutes] = value.split(':').map(Number)
  return (hours * 3600) + (minutes * 60)
}

function toDisplayDurationTime (value) {
  const totalSeconds = Number(value)
  if (!value || Number.isNaN(totalSeconds)) {
    return ''
  }
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function mapActivityProduct (raw) {
  raw = raw || {}
  return {
    product_id: raw.product_id ?? raw.id ?? null,
    description: raw.description ?? '',
    barcode: raw.barcode ?? null,
    quantity: raw.quantity ?? null
  }
}

// Photos taken while executing the activity. The backend hangs them off the
// activity's requirements (`activity_requirements[].completed[]`) rather than off
// the activity itself - flatten them into one list for the preview dialog.
// A fulfillment carries either an uploaded `file` or a `signature` drawn on the
// spot (delivered as a base64 `url`), never both.
// `title` travels with each image so the preview dialog can name whichever one is
// currently on screen: a signature is identified by its own name, an uploaded
// photo - which has none - by when it was taken.
function mapActivityPhotos (raw, translate) {
  const photoLabel = translate(`${TRANSLATION_NAMESPACE}.photo`)
  const buildTitle = (subject) => subject ? `${photoLabel} - ${subject}` : photoLabel

  return (raw?.activity_requirements || []).flatMap((requirement) => {
    return (requirement?.completed || []).flatMap((fulfillment) => {
      const { file, signature, created_at: createdAt } = fulfillment || {}

      if (signature?.url) {
        return [{
          id: signature.id ?? null,
          url: signature.url,
          original_url: signature.url,
          title: buildTitle(signature.name)
        }]
      }

      if (file?.url) {
        return [{
          id: file.id,
          url: file.url,
          original_url: file.url,
          title: buildTitle(toDisplayDateTime(createdAt))
        }]
      }

      return []
    })
  })
}

function mapActivity (raw, translate) {
  raw = raw || {}
  return {
    id: raw.id ?? null,
    uid: raw.id ? `activity-${raw.id}` : nextUid('activity'),
    editing: false,
    activity_id: raw.activity_id ?? null,
    instructions: raw.instructions ?? '',
    actual_start: toDisplayDateTime(raw.actual_start),
    actual_end: toDisplayDateTime(raw.actual_end),
    status_label: raw.status?.description ? translate(raw.status.description) : '',
    productsEditing: false,
    products: (raw.products || []).map(mapActivityProduct),
    photos: mapActivityPhotos(raw, translate),
    validationErrors: {}
  }
}

// The `plan-locations` save endpoint nests address/lat/lng under `geolocation`,
// while the `projects/{id}/detailed` endpoint flattens them onto the location
// itself — fall back to the nested shape so both responses map the same way.
function mapLocation (raw, translate) {
  raw = raw || {}
  return {
    id: raw.id ?? null,
    uid: raw.id ? `location-${raw.id}` : nextUid('location'),
    editing: false,
    geolocation_id: raw.geolocation_id ?? raw.geolocation?.id ?? null,
    geolocation_label: raw.geolocation_label ?? raw.geolocation?.description ?? raw.address ?? raw.geolocation?.address ?? '',
    type_id: raw.type_id ?? null,
    crew_id: raw.crew_id ?? null,
    address: raw.address ?? raw.geolocation?.address ?? '',
    latitude: raw.latitude ?? raw.geolocation?.latitude ?? null,
    longitude: raw.longitude ?? raw.geolocation?.longitude ?? null,
    save_location: raw.save_location ?? false,
    description: raw.description ?? '',
    planned_start: toDisplayDateTime(raw.planned_start),
    planned_duration: toDisplayDurationTime(raw.planned_duration),
    actual_start: toDisplayDateTime(raw.actual_start),
    actual_end: toDisplayDateTime(raw.actual_end),
    status_label: raw.status?.description ? translate(raw.status.description) : '',
    validationErrors: {},
    expanded: true,
    activities: (raw.activities || []).map((activity) => mapActivity(activity, translate))
  }
}

function mapPhase (raw, translate) {
  raw = raw || {}
  return {
    id: raw.id ?? null,
    uid: raw.id ? `phase-${raw.id}` : nextUid('phase'),
    editing: false,
    name: raw.name ?? '',
    type_id: raw.type_id ?? null,
    planned_start: toDisplayDateTime(raw.planned_start),
    planned_end: toDisplayDateTime(raw.planned_end),
    actual_start: toDisplayDateTime(raw.actual_start),
    actual_end: toDisplayDateTime(raw.actual_end),
    status_label: raw.status?.description ? translate(raw.status.description) : '',
    validationErrors: {},
    locations: (raw.locations || raw.planLocations || []).map((location) => mapLocation(location, translate))
  }
}

function createActivity (translate) {
  const activity = mapActivity(null, translate)
  activity.editing = true
  return activity
}

function createLocation (translate) {
  const location = mapLocation(null, translate)
  location.editing = true
  return location
}

function createPhase (translate) {
  const phase = mapPhase(null, translate)
  phase.editing = true
  return phase
}

// The project's roads are read-only on this screen and each one is rendered as
// its own text field, which FieldsRenderer binds to a flat form-data key - so
// alongside the `roads` array itself every name gets a `road_name_{index}`
// entry (see ROAD_NAME_KEY_PREFIX in useRequestDetailsFields.js).
function mapRoads (roads) {
  const list = isArray(roads) ? roads : []

  return {
    roads: list.map((road) => ({
      id: road?.id ?? null,
      name: road?.name ?? ''
    })),
    ...list.reduce((keys, road, index) => {
      keys[`${ROAD_NAME_KEY_PREFIX}${index}`] = road?.name ?? ''
      return keys
    }, {})
  }
}

function createRequestDetails () {
  return {
    status_id: null,
    status_label: '',
    type_id: null,
    planned_start: '',
    planned_end: '',
    actual_start: '',
    actual_end: '',
    address: '',
    city_district_id: null,
    construction_site_id: null,
    latitude: null,
    longitude: null,
    roads: [],
    validationErrors: {}
  }
}

function mapProjectSummary (data, translate) {
  data = data || {}
  return {
    working_order_id: data.id ?? '',
    status_label: data.status?.description ? translate(data.status.description) : '',
    type_label: data.type?.description ? translate(data.type.description) : '',
    // The project's own geolocation (`/detailed`'s top-level `geolocation`) -
    // used to pre-fill a brand new location's picker, since a new location
    // most commonly belongs at the request's own address.
    geolocation: data.geolocation ?? null,
    request_details: {
      status_id: data.status?.id ?? null,
      status_label: data.status?.description ? translate(data.status.description) : '',
      type_id: data.type?.id ?? null,
      planned_start: toDisplayDateTime(data.planned_start),
      planned_end: toDisplayDateTime(data.planned_end),
      actual_start: toDisplayDateTime(data.actual_start),
      actual_end: toDisplayDateTime(data.actual_end),
      address: data.address ?? '',
      city_district_id: data.city_district_id ?? null,
      construction_site_id: data.construction_site_id ?? null,
      latitude: data.latitude ?? null,
      longitude: data.longitude ?? null,
      ...mapRoads(data.roads),
      validationErrors: {}
    }
  }
}

export function useWorkOrderDetails (options) {
  const { translate, resolveRouteParamValue } = options || {}
  const { route } = useAppContext()

  const api = useWorkOrderDetailsApi()
  const { state: infoDialogState, open: openInfoDialog, close: closeInfoDialog, setLoader: setInfoDialogLoader } = useInfoDialog()

  const edit = ref(false)
  const isLoading = ref(false)

  const formData = reactive({
    id: resolveRouteParamValue ? resolveRouteParamValue('id') : null,
    working_order_id: '',
    status_label: '',
    type_label: '',
    geolocation: null,
    request_details: createRequestDetails(),
    phases: []
  })

  const activePhaseUid = ref(null)
  // Set when this screen was opened via a "plan location" deep link (e.g. from
  // crew scheduling's double-click-to-open-work-order) — tells PhasesSection
  // which location tab to auto-select, scroll to and highlight once loaded.
  const targetLocationUid = ref(null)

  const activePhase = computed(() => {
    return formData.phases.find((phase) => phase.uid === activePhaseUid.value) || formData.phases[0]
  })

  // True whenever a phase/location/activity (or the top block) is currently being
  // added or edited, so every OTHER edit/add action on the screen can be disabled
  // while that single edit is in progress.
  const isAnyEditing = computed(() => {
    if (edit.value) {
      return true
    }

    return formData.phases.some((phase) => {
      if (phase.editing) {
        return true
      }
      return phase.locations.some((location) => {
        if (location.editing) {
          return true
        }
        return location.activities.some((activity) => activity.editing || activity.productsEditing)
      })
    })
  })

  const fetchDetails = async () => {
    if (!formData.id) {
      return
    }

    try {
      isLoading.value = true
      const data = await api.fetchDetailed(formData.id)
      const mapped = mapProjectSummary(data, translate)
      mapped.phases = (data?.phases || []).map((phase) => mapPhase(phase, translate))
      Object.assign(formData, mapped)

      const targetPlanLocationId = route?.query?.plan_location_id ? String(route.query.plan_location_id) : null
      const targetPhase = targetPlanLocationId
        ? formData.phases.find((phase) => phase.locations.some((location) => String(location.id) === targetPlanLocationId))
        : null

      if (targetPhase) {
        activePhaseUid.value = targetPhase.uid
        targetLocationUid.value = `location-${targetPlanLocationId}`
      }
      else {
        activePhaseUid.value = formData.phases[0]?.uid ?? null
      }
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      isLoading.value = false
    }
  }

  onMounted(() => {
    fetchDetails()
  })

  // ---- Phases ----

  const addPhase = () => {
    const newPhase = createPhase(translate)
    formData.phases.push(newPhase)
    activePhaseUid.value = newPhase.uid
  }

  const startEditPhase = (phase) => {
    phase._snapshot = cloneDeep({
      name: phase.name,
      type_id: phase.type_id,
      planned_start: phase.planned_start,
      planned_end: phase.planned_end
    })
    phase.validationErrors = {}
    phase.editing = true
  }

  const cancelEditPhase = (phase) => {
    const isNew = !phase.id
    if (isNew) {
      removePhaseLocally(phase)
      return
    }
    if (phase._snapshot) {
      Object.assign(phase, phase._snapshot)
    }
    phase.validationErrors = {}
    phase.editing = false
  }

  const saveEditPhase = async (phase) => {
    try {
      isLoading.value = true
      const payload = {
        project_id: formData.id,
        name: phase.name,
        type_id: phase.type_id,
        planned_start: toApiDateTime(phase.planned_start),
        planned_end: toApiDateTime(phase.planned_end)
      }
      const data = await api.savePhase(formData.id, phase.id, payload)
      // `mapPhase` always remaps `locations` into brand new objects. Feeding
      // it `phase.locations` (as before) would replace every location - and,
      // transitively, every activity - with a new object identity even though
      // only the phase's own fields changed here. Child components (ItemBlock/
      // ActivityBlock) stay mounted across that swap (same `uid`, so Vue's
      // `key` reuses them) but their field composables captured the OLD
      // location/activity object at setup time - so they'd go stale and stop
      // reacting to `.editing`, which is why a child's "hide while editing"
      // fields stopped hiding after a parent save. Preserve the existing
      // location objects untouched instead.
      const { locations } = phase
      Object.assign(phase, mapPhase(data, translate))
      phase.locations = locations
      phase.editing = false
    }
    catch (exception) {
      console.error(exception)
      phase.validationErrors = exception?.getValidationErrors?.() || exception?.response?.data?.errors || {}
    }
    finally {
      isLoading.value = false
    }
  }

  const removePhaseLocally = (phase) => {
    const index = formData.phases.findIndex((item) => item.uid === phase.uid)
    if (index === -1) {
      return
    }
    formData.phases.splice(index, 1)
    if (activePhaseUid.value === phase.uid) {
      activePhaseUid.value = formData.phases[0]?.uid ?? null
    }
  }

  const performRemovePhase = async (phase) => {
    try {
      setInfoDialogLoader(true)
      if (phase.id) {
        await api.deletePhase(formData.id, phase.id)
      }
      removePhaseLocally(phase)
      closeInfoDialog()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoader(false)
    }
  }

  const removePhase = (phase) => {
    if (!phase.id) {
      removePhaseLocally(phase)
      return
    }
    openInfoDialog({
      title: translate(`${TRANSLATION_NAMESPACE}.delete_phase`),
      message: translate(`${TRANSLATION_NAMESPACE}.delete_phase_confirm_message`),
      onConfirm: () => performRemovePhase(phase)
    })
  }

  // ---- Locations ----

  const addLocation = (phase) => {
    const newLocation = createLocation(translate)
    phase.locations.push(newLocation)
    return newLocation
  }

  const startEditLocation = (location) => {
    location._snapshot = cloneDeep({
      geolocation_id: location.geolocation_id,
      geolocation_label: location.geolocation_label,
      type_id: location.type_id,
      crew_id: location.crew_id,
      address: location.address,
      latitude: location.latitude,
      longitude: location.longitude,
      save_location: location.save_location,
      description: location.description,
      planned_start: location.planned_start,
      planned_duration: location.planned_duration
    })
    location.validationErrors = {}
    location.editing = true
  }

  const cancelEditLocation = (phase, location) => {
    const isNew = !location.id
    if (isNew) {
      removeLocationLocally(phase, location)
      return
    }
    if (location._snapshot) {
      Object.assign(location, location._snapshot)
    }
    location.validationErrors = {}
    location.editing = false
  }

  const saveEditLocation = async (phase, location) => {
    try {
      isLoading.value = true
      const payload = {
        project_id: formData.id,
        phase_id: phase.id,
        geolocation_id: location.geolocation_id,
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
        save_location: !!location.save_location,
        description: location.description,
        type_id: location.type_id,
        crew_id: location.crew_id,
        planned_start: toApiDateTime(location.planned_start),
        planned_duration: toApiDurationSeconds(location.planned_duration)
      }
      const data = await api.saveLocation(location.id, payload)
      // Same reasoning as `saveEditPhase` above, one level down: don't let
      // `mapLocation` remap `activities` into new objects and stale out
      // ActivityBlock's field composable closures - keep the existing
      // activity objects untouched.
      const { activities } = location
      Object.assign(location, mapLocation(data, translate))
      location.activities = activities
      location.editing = false
    }
    catch (exception) {
      console.error(exception)
      location.validationErrors = exception?.getValidationErrors?.() || exception?.response?.data?.errors || {}
    }
    finally {
      isLoading.value = false
    }
  }

  const removeLocationLocally = (phase, location) => {
    const index = phase.locations.findIndex((item) => item.uid === location.uid)
    if (index === -1) {
      return
    }
    phase.locations.splice(index, 1)
  }

  const performRemoveLocation = async (phase, location) => {
    try {
      setInfoDialogLoader(true)
      if (location.id) {
        await api.deleteLocation(location.id)
      }
      removeLocationLocally(phase, location)
      closeInfoDialog()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoader(false)
    }
  }

  const removeLocation = (phase, location) => {
    if (!location.id) {
      removeLocationLocally(phase, location)
      return
    }
    openInfoDialog({
      title: translate(`${TRANSLATION_NAMESPACE}.delete_location`),
      message: translate(`${TRANSLATION_NAMESPACE}.delete_location_confirm_message`),
      onConfirm: () => performRemoveLocation(phase, location)
    })
  }

  // ---- Activities ----

  const addActivity = (location) => {
    const newActivity = createActivity(translate)
    location.activities.push(newActivity)
    return newActivity
  }

  const startEditActivity = (activity) => {
    activity._snapshot = cloneDeep({
      activity_id: activity.activity_id,
      products: activity.products
    })
    activity.validationErrors = {}
    activity.editing = true
  }

  const cancelEditActivity = (location, activity) => {
    const isNew = !activity.id
    if (isNew) {
      removeActivityLocally(location, activity)
      return
    }
    if (activity._snapshot) {
      Object.assign(activity, activity._snapshot)
    }
    activity.validationErrors = {}
    activity.editing = false
  }

  const saveEditActivity = async (location, activity) => {
    try {
      isLoading.value = true
      const payload = {
        plan_location_id: location.id,
        activity_id: activity.activity_id,
        instructions: activity.instructions ?? ''
      }
      const data = await api.saveActivity(activity.id, payload)
      // The save response carries neither products nor the requirement
      // fulfillments the photos are derived from - keep both, or editing an
      // activity would blank out its photo field until the next full refresh.
      const { photos } = activity
      Object.assign(activity, mapActivity({ ...data, products: activity.products }, translate))
      activity.photos = photos
      activity.editing = false
    }
    catch (exception) {
      console.error(exception)
      activity.validationErrors = exception?.getValidationErrors?.() || exception?.response?.data?.errors || {}
    }
    finally {
      isLoading.value = false
    }
  }

  const removeActivityLocally = (location, activity) => {
    const index = location.activities.findIndex((item) => item.uid === activity.uid)
    if (index === -1) {
      return
    }
    location.activities.splice(index, 1)
  }

  const performRemoveActivity = async (location, activity) => {
    try {
      setInfoDialogLoader(true)
      if (activity.id) {
        await api.deleteActivity(activity.id)
      }
      removeActivityLocally(location, activity)
      closeInfoDialog()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoader(false)
    }
  }

  const removeActivity = (location, activity) => {
    if (!activity.id) {
      removeActivityLocally(location, activity)
      return
    }
    openInfoDialog({
      title: translate(`${TRANSLATION_NAMESPACE}.delete_activity`),
      message: translate(`${TRANSLATION_NAMESPACE}.delete_activity_confirm_message`),
      onConfirm: () => performRemoveActivity(location, activity)
    })
  }

  // ---- Activity products ----

  const startEditProducts = (activity) => {
    activity._productsSnapshot = cloneDeep(activity.products)
    activity.productsEditing = true
  }

  const cancelEditProducts = (activity) => {
    if (activity._productsSnapshot) {
      activity.products = cloneDeep(activity._productsSnapshot)
    }
    activity.productsEditing = false
  }

  // `products` is the already-validated, merged (existing + newly added)
  // list ProductsListSection hands back via ActivityBlock.vue - not read off
  // `activity.products` here, since pending "new" rows live only inside that
  // component until save.
  const saveEditProducts = async (activity, products) => {
    try {
      isLoading.value = true
      const payload = (products || []).map((product) => ({
        product_id: Number(product.product_id),
        quantity: Number(product.quantity)
      }))
      await api.syncProducts(activity.id, payload)
      activity.products = products.map(mapActivityProduct)
      activity.productsEditing = false
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      isLoading.value = false
    }
  }

  const copyProductsFromOffer = async (activity) => {
    try {
      isLoading.value = true
      const data = await api.copyProductsFromOffer(activity.id)
      const products = isArray(data) ? data : (data?.products || [])
      activity.products = products.map(mapActivityProduct)
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      isLoading.value = false
    }
  }

  // ---- Project (top block) ----

  let requestDetailsSnapshot = null

  const onEditClick = () => {
    requestDetailsSnapshot = cloneDeep(formData.request_details)
    formData.request_details.validationErrors = {}
    edit.value = true
  }

  const onCancelTabSave = (event) => {
    const { cancelTabSave } = event || {}
    if (requestDetailsSnapshot) {
      Object.assign(formData.request_details, requestDetailsSnapshot)
      requestDetailsSnapshot = null
    }
    formData.request_details.validationErrors = {}
    edit.value = false
    if (typeof cancelTabSave === 'function') {
      cancelTabSave()
    }
  }

  const onSaveClick = async (event) => {
    const { saveTab } = event || {}
    try {
      isLoading.value = true
      const { request_details: requestDetails } = formData
      const data = await api.saveProject(formData.id, {
        planned_start: toApiDateTime(requestDetails.planned_start),
        planned_end: toApiDateTime(requestDetails.planned_end),
        latitude: requestDetails.latitude,
        longitude: requestDetails.longitude,
        address: requestDetails.address,
        city_district_id: requestDetails.city_district_id,
        construction_site_id: requestDetails.construction_site_id,
        status_id: requestDetails.status_id,
        type_id: requestDetails.type_id
      })
      // Unlike `/detailed` (used by `fetchDetails`), the save endpoint's
      // response doesn't include the project's own `geolocation` - preserve
      // whatever was already fetched instead of letting it get wiped to null.
      const { geolocation } = formData
      const mapped = mapProjectSummary(data, translate)
      Object.assign(formData, mapped)
      formData.geolocation = geolocation
      requestDetailsSnapshot = null
      edit.value = false
      if (typeof saveTab === 'function') {
        saveTab()
      }
    }
    catch (exception) {
      console.error(exception)
      // Keep edit mode open on a validation error - the user needs to see
      // and fix the offending fields, not get bounced back to read-only view.
      formData.request_details.validationErrors = exception?.getValidationErrors?.() || exception?.response?.data?.errors || {}
    }
    finally {
      isLoading.value = false
    }
  }

  const tabActionsConfig = computed(() => ({
    title: translate(`${TRANSLATION_NAMESPACE}.work_order_details`),
    // `WorkOrderDetailsContent.vue`'s own wrapper already has 16px of padding -
    // stacked with `v-card-title`'s own default Vuetify padding, this header
    // row sat visibly more inset than the summary/request-details blocks
    // below it. Flatten it here instead of globally, since other TabActionButtons
    // consumers rely on the default padding.
    titleCardStyle: { padding: '0 0 16px' },
    showBackButton: {
      uiViewName: 'TemporaryRegulationsWorkOrders'
    },
    // The request-details edit/cancel/save buttons moved into the Basic Info
    // tab (the only editable one of the Request details tabs) - this row keeps
    // just the back button and the "refresh data" action.
    showEditButtons: false,
    additional_buttons: [
      {
        type: 'base',
        label: translate(`${TRANSLATION_NAMESPACE}.refresh_data`),
        prependIcon: 'mdi-refresh',
        disabled: isAnyEditing.value || isLoading.value,
        click: () => fetchDetails()
      }
    ],
    onEditClick,
    onCancelTabSave,
    onSaveClick
  }))

  const updateData = (data) => {
    Object.assign(formData, data || {})
  }

  return {
    edit,
    isLoading,
    isAnyEditing,
    infoDialogState,
    closeInfoDialog,
    formData,
    tabActionsConfig,
    activePhaseUid,
    targetLocationUid,
    activePhase,
    fetchDetails,
    addPhase,
    startEditPhase,
    cancelEditPhase,
    saveEditPhase,
    removePhase,
    addLocation,
    startEditLocation,
    cancelEditLocation,
    saveEditLocation,
    removeLocation,
    addActivity,
    startEditActivity,
    cancelEditActivity,
    saveEditActivity,
    removeActivity,
    startEditProducts,
    cancelEditProducts,
    saveEditProducts,
    copyProductsFromOffer,
    updateData
  }
}
